/**
 * Pure layout math for a left-to-right single-elimination bracket.
 *
 * Shared by the on-screen React bracket and the print/PDF builder so both draw
 * identical geometry (match boxes + elbow connectors), independent of theme.
 * No DOM, no measuring — everything is derived deterministically from the rounds,
 * which lets the print path (a plain HTML string) place connectors precisely.
 */

export interface BracketCompetitor {
  name: string;
  seed: number | null;
  /** Games per set from this competitor's perspective (e.g. [6, 6] or [4, 6, 3]). */
  games: number[];
  isWinner: boolean;
  /** Etiqueta del perdedor cuando el partido no se completó: "RET", "W/O", "DEF". */
  tag?: string | null;
}

export interface BracketMatch {
  id: string;
  /** Real match id (for click-to-edit); null for placeholder/pending slots. */
  matchId?: number | null;
  /** Número de partido (match_number): referencia cruzada con el calendario
   * ("M5" identifica al Por definir vs Por definir). */
  number?: number | null;
  top: BracketCompetitor | null;
  bottom: BracketCompetitor | null;
  /** Optional provenance rendered for a pending side (e.g. winner of preliminary match). */
  topSource?: string;
  bottomSource?: string;
  /** True when the pairing isn't decided yet (placeholder slot). */
  pending: boolean;
}

export interface BracketRound {
  label: string;
  matches: BracketMatch[];
}

export interface BracketDims {
  boxW: number;
  boxH: number;
  colStep: number;
  /** Center-to-center vertical spacing of first-round matches. */
  unitY: number;
  padTop: number;
  padBottom: number;
}

export const DEFAULT_BRACKET_DIMS: BracketDims = {
  boxW: 222,
  boxH: 80,
  colStep: 272,
  unitY: 108,
  padTop: 44,
  padBottom: 30,
};

export interface PositionedMatch {
  match: BracketMatch;
  round: number;
  indexInRound: number;
  /** Top-left corner. */
  x: number;
  y: number;
  /** Vertical center. */
  cy: number;
  isFinal: boolean;
}

export interface BracketLayout {
  width: number;
  height: number;
  matches: PositionedMatch[];
  /** SVG path `d` strings for the round-to-round elbow connectors. */
  connectors: string[];
  roundLabels: { text: string; x: number }[];
  /** The tournament winner, if the final is decided. */
  champion: BracketCompetitor | null;
  finalMatch: PositionedMatch | null;
}

function centerY(indexInRound: number, count: number, sideH: number, padTop: number): number {
  return padTop + ((indexInRound + 0.5) / count) * sideH;
}

/** Rounded-elbow SVG path from (x1,y1) out to a midpoint gutter, down/up, into (x2,y2). */
export function elbowPath(x1: number, y1: number, x2: number, y2: number, r = 10): string {
  const mx = (x1 + x2) / 2;
  if (Math.abs(y1 - y2) < 1) return `M ${x1},${y1} L ${x2},${y2}`;
  const vd = y2 > y1 ? 1 : -1;
  return (
    `M ${x1},${y1} L ${mx - r},${y1} Q ${mx},${y1} ${mx},${y1 + r * vd} ` +
    `L ${mx},${y2 - r * vd} Q ${mx},${y2} ${mx + r},${y2} L ${x2},${y2}`
  );
}

export type BracketVisualSide = "top" | "bottom";

const REGULAR_VISUAL_SIDE_ORDER = ["top", "bottom"] as const;
const SEED_TWO_BOTTOM_VISUAL_SIDE_ORDER = ["bottom", "top"] as const;

/** Orden compartido por superficies que deben conservar sus lados de datos
 * (player1/player2) pero dibujarlos con el seed 2 en la línea inferior. */
export function bracketVisualSideOrder(topSeed: number | null | undefined): readonly [BracketVisualSide, BracketVisualSide] {
  return topSeed === 2 ? SEED_TWO_BOTTOM_VISUAL_SIDE_ORDER : REGULAR_VISUAL_SIDE_ORDER;
}

/** Costumbre del draw (pedido Eduardo 2026-07-27): el sembrado 2 va SIEMPRE
 * en la línea de ABAJO de su matchup — como en cualquier drawsheet impreso
 * (el 1 encabeza el cuadro por arriba; el 2 lo cierra por abajo). Es un
 * acomodo puramente VISUAL: los datos del match (player1/player2) no
 * cambian. Cubre los caminos que arman el par con "player1 = mejor seed"
 * (BYEs, esqueleto, playoffs de fase), donde el 2 quedaba arriba. Las
 * fuentes ("Por definir · G M2") viajan con su globo. */
export function withSeedTwoAtBottom(match: BracketMatch): BracketMatch {
  if (bracketVisualSideOrder(match.top?.seed)[0] === "bottom") {
    return {
      ...match,
      top: match.bottom,
      bottom: match.top,
      topSource: match.bottomSource,
      bottomSource: match.topSource,
    };
  }
  return match;
}

export function computeBracketLayout(rounds: BracketRound[], dims: BracketDims = DEFAULT_BRACKET_DIMS): BracketLayout {
  const empty: BracketLayout = {
    width: 0, height: 0, matches: [], connectors: [], roundLabels: [], champion: null, finalMatch: null,
  };
  if (!rounds.length) return empty;
  // Acomodo visual de costumbre ANTES de posicionar: aplica a todas las
  // superficies (bracket admin, vista jugador, previews y PDF comparten
  // esta geometría).
  rounds = rounds.map((round) => ({
    ...round,
    matches: round.matches.map(withSeedTwoAtBottom),
  }));

  const { boxW, boxH, colStep, unitY, padTop, padBottom } = dims;
  const baseMatchCount = Math.max(...rounds.map((round) => round.matches.length), 1);
  const sideH = baseMatchCount * unitY;
  const lastRound = rounds.length - 1;
  const height = padTop + sideH + padBottom;

  const positioned: PositionedMatch[][] = rounds.map((round, r) => {
    const count = Math.max(round.matches.length, 1);
    return round.matches.map((match, i) => {
      const cy = centerY(i, count, sideH, padTop);
      return {
        match, round: r, indexInRound: i,
        x: r * colStep, y: cy - boxH / 2, cy, isFinal: r === lastRound,
      };
    });
  });

  const matches = positioned.flat();

  // Elbow connectors — only for clean halving transitions (count[r] === 2*count[r+1]).
  // Bye-heavy preliminary rounds (rare) skip that one transition rather than draw wrong lines.
  const connectors: string[] = [];
  for (let r = 0; r < positioned.length - 1; r++) {
    const cur = positioned[r];
    const next = positioned[r + 1];
    if (cur.length !== next.length * 2) continue;
    next.forEach((tgt, ti) => {
      [cur[ti * 2], cur[ti * 2 + 1]].forEach((feeder) => {
        if (!feeder) return;
        connectors.push(elbowPath(feeder.x + boxW, feeder.cy, tgt.x, tgt.cy));
      });
    });
  }

  const roundLabels = rounds.map((round, r) => ({ text: round.label, x: r * colStep + boxW / 2 }));

  const finalMatch = positioned[lastRound]?.[0] ?? null;
  let champion: BracketCompetitor | null = null;
  if (finalMatch && !finalMatch.match.pending) {
    const { top, bottom } = finalMatch.match;
    champion = top?.isWinner ? top : bottom?.isWinner ? bottom : null;
  }

  const width = lastRound * colStep + boxW + 40;
  return { width, height, matches, connectors, roundLabels, champion, finalMatch };
}
